import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  User,
  UserCredential
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './firebase';

/**
 * Sign up a new user with email and password
 */
export async function signUp(
  email: string, 
  password: string, 
  name: string
): Promise<UserCredential> {
  try {
    // Create the user in Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const { user } = userCredential;
    
    // Update the user's profile with their name
    await updateProfile(user, { displayName: name });
    
    // Create the user document in Firestore
    await setDoc(doc(db, 'users', user.uid), {
      email,
      name,
      preferredChannel: 'web',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      founderStage: 'idea', // Default founder stage
    });
    
    return userCredential;
  } catch (error) {
    console.error('Error during sign up:', error);
    throw error;
  }
}

/**
 * Sign in an existing user with email and password
 */
export async function signIn(email: string, password: string): Promise<UserCredential> {
  return signInWithEmailAndPassword(auth, email, password);
}

/**
 * Sign in with Google
 */
export async function signInWithGoogle(): Promise<UserCredential | null> {
  try {
    const provider = new GoogleAuthProvider();
    
    // On mobile devices, use redirect rather than popup
    const isMobile = window.innerWidth <= 768;
    
    let userCredential: UserCredential;
    
    if (isMobile) {
      // Use redirect for mobile
      await signInWithRedirect(auth, provider);
      // This won't execute until after redirect and user returns
      return null;
    } else {
      // Use popup for desktop
      userCredential = await signInWithPopup(auth, provider);
    }
    
    const { user } = userCredential;
    
    // Check if this is a first-time login
    const userDocRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userDocRef);
    
    if (!userDoc.exists()) {
      // Create a new user document in Firestore for first-time Google sign-ins
      await setDoc(userDocRef, {
        email: user.email,
        name: user.displayName || '',
        preferredChannel: 'web',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        founderStage: 'idea', // Default founder stage
      });
    }
    
    return userCredential;
  } catch (error) {
    console.error('Error during Google sign in:', error);
    throw error;
  }
}

/**
 * Check for redirect result (for mobile Google sign-in)
 */
export async function checkRedirectResult(): Promise<UserCredential | null> {
  try {
    const result = await getRedirectResult(auth);
    if (result) {
      const { user } = result;
      
      // Check if this is a first-time login
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        // Create a new user document in Firestore for first-time Google sign-ins
        await setDoc(userDocRef, {
          email: user.email,
          name: user.displayName || '',
          preferredChannel: 'web',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          founderStage: 'idea', // Default founder stage
        });
      }
      
      return result;
    }
    return null;
  } catch (error) {
    console.error('Error checking redirect result:', error);
    return null;
  }
}

/**
 * Sign out the current user
 */
export async function signOut(): Promise<void> {
  return firebaseSignOut(auth);
}

/**
 * Send a password reset email
 */
export async function resetPassword(email: string): Promise<void> {
  return sendPasswordResetEmail(auth, email);
}

/**
 * Get the current user
 */
export function getCurrentUser(): User | null {
  return auth.currentUser;
} 